/* ============================================================
   The recruitment pipeline, in one place
   ------------------------------------------------------------
   Four endpoints return four disjoint slices of the same
   collection (pool / scheduled / selected / rejected), so
   "what stage is this candidate at" is worked out on the
   client. Everything that answers that question lives here, so
   the tiles, the filters, the table and the cards cannot
   disagree with each other.
   ============================================================ */

/* Rounds come back in insertion order and `round` is stored as a string, so
   anything that reads "the current round" has to sort them itself. */
export const roundsOf = (candidate) =>
    [...(candidate?.interviews || [])].sort(
        (a, b) => Number(a?.round || 0) - Number(b?.round || 0));

/* The round a candidate is actually sitting at: the first one still waiting
   on an outcome, otherwise the most recent. */
export const currentRound = (interviews = []) => {
    const rounds = roundsOf({ interviews });
    return rounds.find((round) => !round?.status) || rounds[rounds.length - 1] || null;
};

/* Where the pipeline ended for this candidate: the round that carries the
   decision, so a card can say more than just "Rejected". */
export const decidingRound = (interviews = []) =>
    [...roundsOf({ interviews })].reverse().find((round) =>
        round?.status === 'Selected' || round?.status === 'Rejected') || null;

/* The round somebody can actually join: the one still open, if it has a link. */
export const joinableRound = (interviews = []) => {
    const round = currentRound(interviews);
    return round?.meetingUrl ? round : null;
};

/* Booking always adds the round after the last one on record — the first for
   somebody straight out of the pool. */
export const nextRoundNumber = (candidate) => {
    const rounds = roundsOf(candidate);
    return rounds.length === 0 ? 1 : Number(rounds[rounds.length - 1]?.round || 0) + 1;
};

/* An interviewer reaches the client two ways: the scheduled list looks the
   employee up, the other three carry only their code. */
export const interviewerOf = (round) => {
    const who = round?.scheduledTo;
    if (!who) return null;
    if (typeof who === 'string') return { employeeName: who, designation: '' };
    return who;
};

/* The stages, in the order a candidate moves through them. `key` doubles as
   the filter value and `accent` as the card's top border. */
export const STAGES = [
    { key: 'pool', label: 'Pool', accent: 'wh-accent-amber', tone: 'is-amber' },
    { key: 'scheduled', label: 'Scheduled', accent: 'wh-accent-blue', tone: 'is-blue' },
    { key: 'selected', label: 'Selected', accent: 'wh-accent-green', tone: 'is-green' },
    { key: 'rejected', label: 'Rejected', accent: 'wh-accent-red', tone: 'is-red' },
];

const STAGE_BY_KEY = STAGES.reduce((all, stage) => ({ ...all, [stage.key]: stage }), {});

export const stageOf = (candidate) => {
    if (candidate?.isSelected) return 'selected';
    if (candidate?.isRejected) return 'rejected';
    return roundsOf(candidate).length > 0 ? 'scheduled' : 'pool';
};

export const accentOf = (candidate) =>
    (STAGE_BY_KEY[stageOf(candidate)] || {}).accent || 'wh-accent-blue';

/* One line that says where a candidate stands, whatever stage they are at.
   A scheduled candidate reads as the round they are on and what that round
   is waiting for — "Round 1 scheduled" — rather than a bare "Scheduled". */
export const statusOf = (candidate) => {
    const stage = stageOf(candidate);
    const rounds = roundsOf(candidate);
    const round = currentRound(rounds);
    const decided = decidingRound(rounds);
    const total = Number(candidate?.job?.numberOfRounds) || 0;

    /* rounds with an outcome recorded, whatever that outcome was */
    const done = rounds.filter((item) => item?.status).length;

    const at = (item) => `Round ${item?.round || '?'}`;

    let label = 'Not scheduled';
    let tone = 'wh-pill-warn';

    if (stage === 'selected') {
        label = decided ? `Selected at ${at(decided).toLowerCase()}` : 'Selected';
        tone = 'wh-pill-good';
    } else if (stage === 'rejected') {
        label = decided ? `Rejected at ${at(decided).toLowerCase()}` : 'Rejected';
        tone = 'wh-pill-danger';
    } else if (stage === 'scheduled' && round) {
        if (!round.status) {
            label = `${at(round)} scheduled`;
            tone = '';
        } else if (round.status === 'Hold') {
            label = `${at(round)} on hold`;
            tone = 'wh-pill-warn';
        } else if (round.status === 'Rejected') {
            label = `${at(round)} not cleared`;
            tone = 'wh-pill-danger';
        } else {
            label = `${at(round)} cleared`;
            tone = 'wh-pill-good';
        }
    }

    return {
        stage,
        stageLabel: (STAGE_BY_KEY[stage] || {}).label || stage,
        accent: (STAGE_BY_KEY[stage] || {}).accent,
        label,
        tone,
        round,
        rounds,
        decided,
        done,
        total,
        interviewer: interviewerOf(round),
        /* the round still open is the only one worth a date and a join link */
        when: round && !round.status ? round.startTime : null,
        meetingUrl: round && !round.status ? round.meetingUrl : '',
    };
};

/* skills are stored as one comma-separated string */
export const splitSkills = (value) =>
    String(value || '').split(',').map((skill) => skill.trim()).filter(Boolean);

export const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : '') +
        (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};
